(() => {
  'use strict';

  if (customElements.get('ops-print-this-artwork')) return;

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const imageUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || value.url || '';
  };

  class OpsPrintThisArtwork extends HTMLElement {
    constructor() {
      super();
      this.artworkId = '';
      this.sourceHandle = '';
      this.objectUrl = '';
      this.record = null;
    }

    connectedCallback() {
      if (this.dataset.opsReady === 'true') return;
      this.dataset.opsReady = 'true';
      this.dataset.artworkReady = 'false';
      this.bindFilters();
      this.bindProductChoices();
      this.initialise().catch((error) => this.fail(error));
    }

    disconnectedCallback() {
      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    }

    async waitForCore() {
      for (let attempt = 0; attempt < 80; attempt += 1) {
        if (window.OPSArtworkLibraryCore?.get && window.OPSArtworkLibraryCore?.saveFile) {
          return window.OPSArtworkLibraryCore;
        }
        await delay(50);
      }
      throw new Error('My Artwork is not available on this page yet.');
    }

    async initialise() {
      const params = new URLSearchParams(window.location.search);
      this.sourceHandle = String(params.get(this.dataset.sourceParam || 'opsSource') || '').trim();
      const requestedArtworkId = String(
        params.get(this.dataset.artworkParam || 'opsArtwork') ||
        localStorage.getItem('opsPendingArtworkId') ||
        ''
      ).trim();

      const core = await this.waitForCore();

      if (requestedArtworkId) {
        const record = await core.get(requestedArtworkId);
        if (record) {
          this.artworkId = record.id;
          this.record = record;
          this.renderRecord(record);
          localStorage.removeItem('opsPendingArtworkId');
          return;
        }
      }

      if (this.sourceHandle) {
        await this.importSourceProduct(core, this.sourceHandle);
        return;
      }

      this.setStatus('Choose artwork in My Artwork before selecting a print format.', true);
      this.querySelector('[data-artwork-placeholder] strong').textContent = 'No artwork selected';
    }

    async importSourceProduct(core, handle) {
      this.setStatus('Loading the selected product artwork…');
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('The selected product artwork could not be loaded.');

      const product = await response.json();
      const candidates = [
        imageUrl(product.featured_image),
        ...(Array.isArray(product.images) ? product.images.map(imageUrl) : [])
      ].filter(Boolean);
      const unique = [...new Set(candidates)];
      const sourceImage = unique.find((url) => /(?:artwork|art-print|print-file|poster)/i.test(url)) || unique[0] || '';
      if (!sourceImage) throw new Error('This product does not have artwork that can be reused yet.');

      const imageResponse = await fetch(new URL(sourceImage, window.location.origin).toString());
      if (!imageResponse.ok) throw new Error('The artwork file could not be imported from the product.');
      const blob = await imageResponse.blob();
      if (!blob.type.startsWith('image/')) throw new Error('The product artwork is not an image file.');

      const extension = this.extensionFor(blob.type);
      const file = new File([blob], handle + '-artwork.' + extension, {
        type: blob.type,
        lastModified: Date.now()
      });

      let record = await core.saveFile(file, {
        name: product.title || handle,
        type: blob.type,
        size: blob.size,
        notes: 'Imported from the ready-made Shopify product ' + (product.title || handle) + '.',
        tags: ['ready-made-artwork', 'source-product:' + handle],
        source: 'shopify-product',
        makerId: 'print-this-artwork',
        route: window.location.pathname
      });

      if (core.put) {
        record = await core.put({
          ...record,
          sourceProductHandle: handle,
          sourceProductTitle: product.title || handle,
          sourceImageUrl: sourceImage
        }, 'source-product');
      }

      this.artworkId = record.id;
      this.record = record;
      this.renderRecord(record);

      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set(this.dataset.artworkParam || 'opsArtwork', record.id);
      window.history.replaceState({}, '', nextUrl.toString());
    }

    extensionFor(type) {
      const map = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/webp': 'webp',
        'image/gif': 'gif',
        'image/svg+xml': 'svg'
      };
      return map[type] || 'png';
    }

    renderRecord(record) {
      const source = record?.originalFile instanceof Blob
        ? record.originalFile
        : record?.file instanceof Blob
          ? record.file
          : null;
      if (!source) throw new Error('The selected artwork file is no longer available in this browser.');

      if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = URL.createObjectURL(source);

      const preview = this.querySelector('[data-artwork-preview]');
      const placeholder = this.querySelector('[data-artwork-placeholder]');
      const title = this.querySelector('[data-artwork-title]');
      const copy = this.querySelector('[data-artwork-copy]');

      if (preview) {
        preview.src = this.objectUrl;
        preview.alt = record.name || 'Selected artwork';
        preview.hidden = false;
      }
      if (placeholder) placeholder.hidden = true;
      if (title) title.textContent = record.name || 'Selected artwork';
      if (copy) copy.textContent = 'Choose another size, frame or print format below. This artwork will follow you into the selected product.';

      this.querySelectorAll('[data-artwork-chip]').forEach((chip) => {
        const image = chip.querySelector('[data-artwork-chip-image]');
        if (image) {
          image.src = this.objectUrl;
          image.alt = record.name || 'Selected artwork';
        }
        chip.hidden = false;
      });

      this.dataset.artworkReady = 'true';
      this.setStatus('Artwork ready. Choose a product below.');
    }

    bindFilters() {
      this.querySelectorAll('[data-category-filter]').forEach((button) => {
        button.addEventListener('click', () => {
          const filter = button.dataset.categoryFilter || 'all';
          this.querySelectorAll('[data-category-filter]').forEach((candidate) => {
            candidate.classList.toggle('is-active', candidate === button);
          });
          this.querySelectorAll('[data-product-card]').forEach((card) => {
            card.hidden = filter !== 'all' && card.dataset.category !== filter;
          });
        });
      });
    }

    bindProductChoices() {
      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-product-choice]');
        if (!link) return;
        if (!this.artworkId) {
          event.preventDefault();
          this.setStatus('Select artwork before choosing another product.', true);
          return;
        }

        event.preventDefault();
        const target = new URL(link.dataset.productUrl || link.href, window.location.origin);
        target.searchParams.set(this.dataset.artworkParam || 'opsArtwork', this.artworkId);
        if (this.sourceHandle) target.searchParams.set(this.dataset.sourceParam || 'opsSource', this.sourceHandle);
        const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewThemeId) target.searchParams.set('preview_theme_id', previewThemeId);
        localStorage.setItem('opsPendingArtworkId', this.artworkId);
        window.location.href = target.toString();
      });
    }

    setStatus(message, isError = false) {
      const status = this.querySelector('[data-artwork-status]');
      if (!status) return;
      status.textContent = message || '';
      status.dataset.error = isError ? 'true' : 'false';
    }

    fail(error) {
      this.dataset.artworkReady = 'false';
      this.setStatus(error?.message || 'The selected artwork could not be loaded.', true);
      const placeholder = this.querySelector('[data-artwork-placeholder]');
      if (placeholder) {
        placeholder.hidden = false;
        const strong = placeholder.querySelector('strong');
        if (strong) strong.textContent = 'Artwork unavailable';
      }
    }
  }

  customElements.define('ops-print-this-artwork', OpsPrintThisArtwork);
})();
